import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { faCartShopping } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import type { RelocationStatus } from '../../models';

interface StatusMessageProps {
  status: RelocationStatus;
  assignedCartLabel?: string;
  assignedLocationOrderId?: number | null;
  dispatchedAt?: string | null;
}

/**
 * Mensaje de estado final (despachado / cancelado) en la columna izquierda.
 * Misma estructura visual que WorkOrderFinishedMessage (extra-work-orders).
 */
export const StatusMessage: React.FC<StatusMessageProps> = ({
  status,
  assignedCartLabel,
  assignedLocationOrderId,
  dispatchedAt,
}) => {
  const { t } = useTranslation('relocationProcessModule');
  const navigate = useNavigate();

  const formatDateTime = (value?: string | null) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleBackToList = () => {
    navigate('/relocations');
  };

  const isDispatched = status === 'dispatched';
  const isCancelled = status === 'cancelled';

  const titleKey = isDispatched
    ? 'finished.dispatched.title'
    : isCancelled
      ? 'finished.cancelled.title'
      : 'finished.draft.title';
  const messageKey = isCancelled ? 'finished.cancelled.message' : 'finished.draft.message';

  const titleClass = isDispatched
    ? 'text-success text-xl font-semibold mb-4'
    : isCancelled
      ? 'text-error text-xl font-semibold mb-2'
      : 'text-title text-xl font-semibold mb-2';

  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <h3 className={titleClass}>{t(titleKey)}</h3>
        {!isDispatched && <p className="text-complementary-text mb-6">{t(messageKey)}</p>}

        {isDispatched && (
          <div className="text-sm text-complementary-text space-y-2 text-center mb-6">
            {assignedCartLabel ? (
              <p className="inline-flex flex-wrap items-center justify-center gap-1.5">
                <span className="text-subtext">{t('panel.assigned_cart')}: </span>
                <span className="inline-flex items-center gap-1.5 text-text font-medium">
                  <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5 text-icons shrink-0" aria-hidden />
                  <span>{assignedCartLabel}</span>
                </span>
              </p>
            ) : null}
            {assignedLocationOrderId !== null && assignedLocationOrderId !== undefined ? (
              <p>
                <span className="text-subtext">{t('panel.location_order_id')}: </span>
                <Link
                  to={`/location-orders/${assignedLocationOrderId}`}
                  className="text-primary font-medium underline underline-offset-2 hover:no-underline"
                  data-testid={`location-order-link-${assignedLocationOrderId}`}
                >
                  #{assignedLocationOrderId}
                </Link>
              </p>
            ) : null}
            <p>
              <span className="text-subtext">{t('panel.dispatched_at')}: </span>
              <span className="text-text font-medium">{formatDateTime(dispatchedAt)}</span>
            </p>
          </div>
        )}

        <Button
          variant="primary"
          onClick={handleBackToList}
          data-testid="relocation-back-to-list-button"
          className="mx-auto"
        >
          {t('finished.back_button')}
        </Button>
      </div>
    </div>
  );
};
